import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import { authCardStyle } from '../constants/authStyles';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import { translateAbilityTypeName } from '../utils/translateGameData';

const AbilityTypeSelection = () => {
  const { t } = useTranslation('selection');
  const { t: tg } = useTranslation('gameData');
  const { t: tc } = useTranslation('common');
  const [abilityTypes, setAbilityTypes] = useState([]);
  const [selectedAbilityType, setSelectedAbilityType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setError, clearError } = usePopupMessage();
  const { refreshCharacter, loading: characterLoading, hasCharacter } = useCharacter();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const characterId = searchParams.get('characterId');
  const characterName = decodeURIComponent(searchParams.get('characterName') || '');
  const username = localStorage.getItem('username');

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (!characterLoading && hasCharacter) {
      navigate('/game', { replace: true });
      return;
    }
    if (!characterId) {
      navigate('/character-selection');
      return;
    }
    if (!characterLoading && !hasCharacter) {
      fetchAbilityTypes();
    }
  }, [username, characterId, navigate, characterLoading, hasCharacter]);

  const fetchAbilityTypes = async () => {
    try {
      const response = await axios.get(
        `/api/ability-types?characterId=${characterId}`
      );
      if (response.data.success) {
        setAbilityTypes(response.data.abilityTypes || []);
        if (!response.data.abilityTypes?.length) {
          setError(t('abilityNotFound'));
        }
      } else {
        setError(response.data.message || t('abilityLoadFailed'));
      }
    } catch (err) {
      setError(t('abilityLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const saveSelection = useCallback(async (abilityType) => {
    if (!abilityType || saving) return;

    setSelectedAbilityType(abilityType);
    setSaving(true);
    clearError();

    try {
      const response = await axios.post('/api/user-character', {
        username,
        characterId: parseInt(characterId, 10),
        abilityTypeId: abilityType.id,
      });

      if (response.data.success) {
        await refreshCharacter();
        navigate('/game');
      } else {
        setError(response.data.message || tc('saveFailed'));
        setSaving(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || tc('errorGeneric'));
      setSaving(false);
    }
  }, [saving, clearError, username, characterId, navigate, refreshCharacter, setError, tc]);

  const handleAbilityTypeSelect = (abilityType) => {
    saveSelection(abilityType);
  };

  const handleBack = () => {
    navigate('/character-selection');
  };

  if (characterLoading || hasCharacter || loading) {
    return (
      <div className="auth-container auth-container--selection">
        <div className="auth-loading">{tc('loading')}</div>
      </div>
    );
  }

  return (
    <div className="auth-container auth-container--selection">
      <div className="auth-card" style={authCardStyle}>
        <AuthSelectionToolbar
          onBack={handleBack}
          title={t('abilityTitle')}
          subtitle={
            <>
              {t('abilitySelectedCharacter')} <strong>{characterName}</strong>
            </>
          }
        />

        <div className="auth-selection-body">
          <p className="auth-info-text">
            {t('abilityHint')}
          </p>
          <div className="selection-grid">
            {abilityTypes.map((abilityType) => {
              const isSelected = selectedAbilityType?.id === abilityType.id;
              const isSavingThis = saving && isSelected;
              const displayName = translateAbilityTypeName(tg, abilityType.name);

              return (
                <div
                  key={abilityType.id}
                  className={`selection-card ${isSelected ? 'selected' : ''} ${saving ? 'selection-card--disabled' : ''}`}
                  onClick={() => !saving && handleAbilityTypeSelect(abilityType)}
                  role="button"
                  tabIndex={saving ? -1 : 0}
                  onKeyDown={(e) => {
                    if (!saving && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleAbilityTypeSelect(abilityType);
                    }
                  }}
                >
                  <div className="selection-card__media">
                    {abilityType.imageUrl ? (
                      <img src={abilityType.imageUrl} alt={displayName} />
                    ) : (
                      <div className="placeholder-icon">🎯</div>
                    )}
                  </div>
                  <h3>{displayName}</h3>
                  <p>{abilityType.description}</p>
                  {isSavingThis && (
                    <p className="selection-card__saving">{t('abilitySaving')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbilityTypeSelection;
